/**
 * NIGHTBOUND - settings.
 *
 * Sound, ambience and motion are real preferences stored on the account (the
 * reduced-motion choice also mirrors the OS setting). The install control only
 * appears when the browser actually offers an install prompt.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGame } from '../state/GameProvider'
import { Button, Chip, Panel, PanelHeading, cx } from '../components/ui'
import { Logo } from '../components/Logo'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function SettingsScreen() {
  const { user, creature, logout, updateSettings } = useGame()
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(false)

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault()
      setInstallEvent(event as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setInstallEvent(null)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    if (window.matchMedia('(display-mode: standalone)').matches) setInstalled(true)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const install = async () => {
    if (!installEvent) return
    await installEvent.prompt()
    const choice = await installEvent.userChoice
    if (choice.outcome === 'accepted') setInstalled(true)
    setInstallEvent(null)
  }

  if (!user) return null
  const settings = user.settings

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <h1 className="font-display text-2xl text-bone-100">Settings</h1>
        <p className="mt-1 text-xs text-ash-400">How NIGHTBOUND behaves on this device.</p>
      </header>

      <div className="space-y-5">
        <Panel className="overflow-hidden">
          <PanelHeading>Game</PanelHeading>
          <div className="divide-y divide-ash-700/30">
            <SettingRow
              label="Sound effects"
              description="Care and interface sounds. Never required."
              value={settings.soundEnabled}
              onChange={(next) => void updateSettings({ soundEnabled: next })}
            />
            <SettingRow
              label="Ambient sound"
              description="A low loop while the shelter is open."
              value={settings.ambienceEnabled}
              onChange={(next) => void updateSettings({ ambienceEnabled: next })}
            />
            <SettingRow
              label="Reduce motion"
              description="Stills the creature and the room. Follows your device setting by default."
              value={settings.reducedMotion}
              onChange={(next) => void updateSettings({ reducedMotion: next })}
            />
          </div>
        </Panel>

        <Panel className="overflow-hidden">
          <PanelHeading right={installed ? <Chip tone="good">installed</Chip> : undefined}>
            App
          </PanelHeading>
          <div className="space-y-3 p-4">
            <p className="text-xs leading-relaxed text-ash-400">
              NIGHTBOUND installs like an app and keeps your creature's state on the server, so it
              carries on living while the tab is closed.
            </p>
            {installed ? (
              <p className="text-xs text-rot-500">Already installed on this device.</p>
            ) : installEvent ? (
              <Button variant="primary" onClick={() => void install()}>
                Install NIGHTBOUND
              </Button>
            ) : (
              <p className="text-[11px] leading-relaxed text-ash-500">
                Use your browser's <span className="text-ash-300">Add to Home Screen</span> /
                <span className="text-ash-300"> Install app</span> option to install it.
              </p>
            )}
          </div>
        </Panel>

        <Panel className="overflow-hidden">
          <PanelHeading>Account</PanelHeading>
          <div className="space-y-3 p-4">
            <dl className="space-y-2 text-xs">
              <Row label="Name" value={user.username} />
              <Row label="Email" value={user.email} />
              <Row
                label="Joined"
                value={new Date(user.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              />
              {creature ? <Row label="Creature" value={`${creature.name} · Lv ${creature.level}`} /> : null}
            </dl>
            <div className="flex flex-wrap gap-2 border-t border-ash-700/40 pt-3">
              <Link
                to="/shelter"
                className="rounded-[3px] border border-ash-600 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-bone-300 hover:bg-night-700"
              >
                Back to shelter
              </Link>
              <Button variant="danger" onClick={() => void logout()}>
                Leave the shelter
              </Button>
            </div>
            <p className="text-[10px] leading-relaxed text-ash-500">
              There is no delete account button in this milestone. Your data lives on the server;
              signing out leaves the creature safe.
            </p>
          </div>
        </Panel>

        <div className="flex flex-col items-center gap-3 pt-2 text-center">
          <Logo size="sm" />
          <p className="nb-label text-[8px] text-ash-600">
            v0.1.0 · first playable milestone · your creature never dies
          </p>
        </div>
      </div>
    </div>
  )
}

function SettingRow({
  label,
  description,
  value,
  onChange,
}: {
  label: string
  description: string
  value: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm text-bone-200">{label}</div>
        <p className="mt-0.5 text-[11px] leading-snug text-ash-400">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        onClick={() => onChange(!value)}
        className={cx(
          'relative h-6 w-11 shrink-0 rounded-full border transition-colors',
          value ? 'border-ember-600/70 bg-ember-500/25' : 'border-ash-600 bg-night-950/70',
        )}
      >
        <span
          aria-hidden
          className={cx(
            'absolute top-0.5 h-4 w-4 rounded-full transition-all',
            value ? 'left-[22px] bg-ember-400' : 'left-0.5 bg-ash-500',
          )}
        />
        <span className="sr-only">{value ? 'On' : 'Off'}</span>
      </button>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-ash-400">{label}</dt>
      <dd className="truncate text-bone-300">{value}</dd>
    </div>
  )
}
